import React, { useEffect, useRef } from "react";
import { useState } from "react";
import { FormikProps } from "formik";
import Styles from "./OtpInputForm.module.scss";
import classnames from "classnames";
//
import TextCTA from "@vahak/core-ui/dist/components/TextCTA";
import Typography from "@vahak/core-ui/dist/components/Typography";
import OTPInput from "@vahak/core-ui/dist/components/OTPInput";
import Button from "@vahak/core-ui/dist/components/Button";
import MobileInputHeader from "../mobile-input/mobile-input-header/MobileInputHeader";
//
import OTPPoster from "@vahak/core/dist/icons/otpPoster.svg";
import WhatsappIcon from "@vahak/core/dist/icons/WhatsappGreen.svg";
import SmsIcon from "@vahak/core/dist/icons/sms.svg";
import COLORS from "@vahak/core-ui/dist/constants/colors";
//
import { otpModel } from "@vahak/core/dist/components/forms/otp-form/otpModel";
import { OtpFormVals } from "@vahak/core/dist/custom-types/singup-form";
import { GenericObject } from "@vahak/core/dist/custom-types/generic-object";
import { OnClickResendOTPParams } from "@vahak/core/dist/components/auth/useAuth";
import { timer } from "@vahak/core/dist/methods/timer";
import { GA4EventNames } from "@vahak/core/dist/constants/event-names";
import { IDs } from "@vahak/core/dist/constants/automation";
import { useEventTrackerService } from "@vahak/core/dist/_services/hooks/useEventTrackerService";

const formatToString = (n: number) => {
    return n.toLocaleString("en-US", {
        minimumIntegerDigits: 2,
        useGrouping: false
    });
};

const getTimeRemaining = (time: number) => {
    const minutes = formatToString(Math.floor((time / 60) % 60));
    const seconds = formatToString(Math.floor(time % 60));
    return {
        seconds,
        minutes
    };
};
export interface OtpInputFormProps {
    formikConfig: FormikProps<OtpFormVals>;
    onClickResendOtp: (p: OnClickResendOTPParams) => void;
    onClickResendOtpViaWA: (p: OnClickResendOTPParams) => void;
    onClickEditNumber?: () => void;
    updateModalTitle?: (title: React.ReactNode) => void;
    toggleModal?: () => void;
    renderPoster?: React.ReactNode;
    ctaText?: string;
}
const OtpInputForm = ({
    formikConfig,
    onClickResendOtp,
    onClickResendOtpViaWA,
    toggleModal,
    onClickEditNumber,
    updateModalTitle,
    renderPoster,
    ctaText = "Verify OTP"
}: OtpInputFormProps) => {
    const bufferTime = 30;
    const otpLength = 4;
    const [timeRemaining, setTimeRemaining] = useState(bufferTime);
    const [isResendingOtp, setIsResendingOtp] = useState(false);
    const otpInputRef = useRef<HTMLInputElement>(null);

    const onChangeOTP = (str: string) => {
        formikConfig.setFieldValue(otpModel.fields.otp, str.trim());
    };

    const autoReadOTP = () => {
        const controller = new AbortController();
        setTimeout(() => {
            controller.abort();
        }, 1 * 60 * 1000);

        if ("OTPCredential" in window) {
            console.log("test------window", window);
            try {
                navigator.credentials
                    .get({ signal: controller.signal, otp: { transport: ["sms"] } })
                    .then((otp) => {
                        console.log("test------otp", otp);
                        if (otp?.type === "otp") {
                            otpInputRef?.current?.blur();
                            formikConfig.setFieldValue(otpModel.fields.otp, otp?.code?.trim());
                            formikConfig.handleSubmit();
                        }
                    })
                    .catch((e) => {
                        console.error("Please try manually with your number.", e);
                    });
            } catch (e) {
                console.error("Please try manually with your number.", e);
                return;
            }
        }
    };

    const successCallback = () => {
        setIsResendingOtp(false);
        timer(bufferTime, (t) => {
            setTimeRemaining(t);
        });
    };

    const { sendGAandMoEngageEvent } = useEventTrackerService();
    const handleEventTracking = (name: string, otherData?: GenericObject) => {
        const data = {
            source_screen: "phone_number_registration",
            phone_number: formikConfig.values.phoneNum,
            ...otherData
        };

        sendGAandMoEngageEvent({
            name,
            data
        });
    };

    const handleSmsResend = () => {
        handleEventTracking(GA4EventNames.LOGIN_OR_SIGNUP_MODAL["otp_via_message"]);

        setIsResendingOtp(true);
        //autoReadOTP();
        onClickResendOtp?.({
            onSuccessCallback: successCallback
        });
    };

    const handleWAResend = () => {
        handleEventTracking(GA4EventNames.LOGIN_OR_SIGNUP_MODAL["otp_via_whatsapp"]);

        setIsResendingOtp(true);
        onClickResendOtp?.({
            isWhatsApp: true,
            onSuccessCallback: successCallback
        });
    };

    useEffect(() => {
        updateModalTitle?.(<MobileInputHeader type="otp-input" onCloseModal={toggleModal!} />);
        //autoReadOTP();
        const timerId = timer(bufferTime, (t) => {
            setTimeRemaining(t);
        });
        return () => clearInterval(timerId);
    }, []);

    const { minutes, seconds } = getTimeRemaining(timeRemaining);

    const disableResendAction = timeRemaining > 0 || isResendingOtp;

    const canContinue =
        String(formikConfig.values.otp).length === otpLength &&
        !(isResendingOtp || formikConfig.isSubmitting || formikConfig.errors.otp);

    return (
        <div className={Styles.main}>
            <div className={Styles.centered}>
                {renderPoster ? (
                    renderPoster
                ) : (
                    <div className={Styles.imgWrapper}>
                        <OTPPoster />
                    </div>
                )}
            </div>
            <div className={Styles.form}>
                <div className={Styles.formHeader}>
                    <div className={Styles.headingLabel}>Verify Mobile Number</div>
                    <div className={Styles.headerAction}>
                        <Typography color={COLORS.GREY_600}>
                            Enter the OTP sent to
                            <Typography weight="medium"> {formikConfig.values.phoneNum}</Typography>
                        </Typography>
                        {onClickEditNumber ? (
                            <TextCTA weight="medium" onClick={onClickEditNumber}>
                                <Typography underline>Edit</Typography>
                            </TextCTA>
                        ) : null}
                    </div>
                </div>
                <div className={Styles.inputGrp}>
                    <form>
                        <OTPInput
                            id={IDs.auth.otp}
                            ref={otpInputRef}
                            length={otpLength}
                            onChange={onChangeOTP}
                            value={String(formikConfig.values.otp ?? "")}
                            errorMsg={String(formikConfig.errors.otp ?? "")}
                        />
                        <button
                            hidden
                            type="submit"
                            onClick={() => formikConfig.handleSubmit()}
                            disabled={!canContinue}
                        >
                            Submit
                        </button>
                    </form>
                </div>
                <div className={Styles.formAction}>
                    {disableResendAction ? (
                        <span className={Styles.timer}>
                            {minutes}:{seconds}
                        </span>
                    ) : (
                        <span className={Styles.resendBtn} onClick={handleSmsResend} id={IDs.auth.resendBtn}>
                            Resend OTP
                        </span>
                    )}
                    <div className={classnames(Styles.btns, disableResendAction ? Styles.btns_disabled : "")}>
                        <TextCTA onClick={handleSmsResend} disabled={disableResendAction} id={IDs.auth.resendBtnSms}>
                            <span className={Styles.btnTextWrapper}>
                                <SmsIcon />
                                <span className={Styles.btnText}>OTP via SMS</span>
                            </span>
                        </TextCTA>
                        <TextCTA onClick={handleWAResend} disabled={disableResendAction} id={IDs.auth.resendBtnWa}>
                            <span className={Styles.btnTextWrapper}>
                                <WhatsappIcon />
                                <span className={Styles.btnText}>OTP via WhatsApp</span>
                            </span>
                        </TextCTA>
                    </div>
                </div>
            </div>
            <Button
                id={IDs.auth.submitBtn}
                blockBtn
                onClick={() => formikConfig.handleSubmit()}
                disabled={!canContinue}
                type="submit"
            >
                {ctaText}
            </Button>
        </div>
    );
};

export default OtpInputForm;
